import React from 'react';
import type { CRMUser, LeadComm, PdfAnnotation, ProjectMeetingNote, ProjectTask } from '../../types';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import type { GroupCommentContext, GroupMemberTaskRow } from '../../utils/groupComments';
import TaskComments, { type GroupCommentsTab } from './TaskComments';

export interface GroupCommentsPopoutProps {
  groupContext: GroupCommentContext | null;
  popoutToken: number | null;
  dealId?: string;
  /** All PDF annotations for the group's project (PDF Comments tab). */
  projectPdfAnnotations?: PdfAnnotation[];
  /** Fathom meeting notes for the group's project (PM-only). */
  projectMeetingNotes?: ProjectMeetingNote[];
  /** Lead-scoped comms carried over after conversion (same docs; no copy). */
  leadComms?: LeadComm[];
  leadIdForComms?: string | null;
  isProjectManager?: boolean;
  highlightCommentId?: string | null;
  onHighlightCommentConsumed?: () => void;
  /** When the popout opens, auto-open the plan review summary modal for this review id. */
  initialPlanReviewId?: string | null;
  onInitialPlanReviewConsumed?: () => void;
  onClose: () => void;
  groupMemberTasks?: GroupMemberTaskRow[];
  teamMembers?: Pick<CRMUser, 'uid' | 'displayName' | 'role' | 'company'>[];
  currentUserId?: string;
  onUpdateTask?: (taskId: string, updates: Partial<ProjectTask>) => void | Promise<void>;
  onCompleteTask?: (taskId: string) => void;
  onOpenPdf?: (
    url: string,
    title: string,
    documentId: string,
    options?: { annotationId?: string; pageNumber?: number },
  ) => void;
  onOpenImage?: (
    url: string,
    title: string,
    documentId: string,
    options?: { annotationId?: string; mimeType?: string },
  ) => void;
  onOpenCsv?: (
    url: string,
    title: string,
    documentId: string,
    options?: { annotationId?: string; mimeType?: string },
  ) => void;
  readOnly?: boolean;
  architectSurface?: boolean;
  initialTab?: GroupCommentsTab | null;
  structureMentionDealId?: string;
  structureMentions?: StructureRef[];
  structuresByDealId?: Map<string, StructureRef[]>;
}

/** Full-screen consolidated group comments modal (Comments + PDF Comments tabs). */
const GroupCommentsPopout: React.FC<GroupCommentsPopoutProps> = ({
  groupContext,
  popoutToken,
  dealId,
  projectPdfAnnotations,
  projectMeetingNotes,
  leadComms,
  leadIdForComms,
  isProjectManager,
  highlightCommentId,
  onHighlightCommentConsumed,
  initialPlanReviewId,
  onInitialPlanReviewConsumed,
  onClose,
  groupMemberTasks,
  teamMembers,
  currentUserId,
  onUpdateTask,
  onCompleteTask,
  onOpenPdf,
  onOpenImage,
  onOpenCsv,
  readOnly = false,
  architectSurface = false,
  initialTab = null,
  structureMentionDealId,
  structureMentions,
  structuresByDealId,
}) => {
  if (!groupContext) return null;

  return (
    <TaskComments
      key={groupContext.groupKey}
      taskId=""
      taskTitle={groupContext.displayName}
      projectId={groupContext.primaryProjectId}
      dealId={dealId}
      groupContext={groupContext}
      projectPdfAnnotations={projectPdfAnnotations}
      projectMeetingNotes={projectMeetingNotes}
      leadComms={leadComms}
      leadIdForComms={leadIdForComms}
      isProjectManager={isProjectManager}
      highlightCommentId={highlightCommentId}
      onHighlightCommentConsumed={onHighlightCommentConsumed}
      initialPlanReviewId={initialPlanReviewId}
      onInitialPlanReviewConsumed={onInitialPlanReviewConsumed}
      popoutRequestToken={popoutToken}
      onPopoutClose={onClose}
      groupMemberTasks={groupMemberTasks}
      teamMembers={teamMembers}
      currentUserId={currentUserId}
      onUpdateTaskForGroup={onUpdateTask}
      onCompleteTaskForGroup={onCompleteTask}
      onOpenPdf={onOpenPdf}
      onOpenImage={onOpenImage}
      onOpenCsv={onOpenCsv}
      readOnly={readOnly}
      architectSurface={architectSurface}
      initialTab={initialTab}
      structureMentionDealId={structureMentionDealId}
      structureMentions={structureMentions}
      structuresByDealId={structuresByDealId}
    />
  );
};

export default GroupCommentsPopout;
